/**
 * useFocusTrap — Tastatur-Fokus innerhalb eines Containers halten.
 *
 * Pflicht für Modale (WCAG 2.1 AA / BITV 2.0, Punkt 2.1.2 No Keyboard Trap +
 * 2.4.3 Focus Order). Verhindert, dass der User per Tab aus dem geöffneten
 * Modal in die dahinter liegende Seite springt.
 *
 * Funktionsweise:
 *   1. Beim Mount: ersten fokussierbaren Eintrag im Container fokussieren
 *      (oder den expliziten `initialFocusRef`, falls gesetzt).
 *   2. Tab/Shift+Tab innerhalb des Containers wrappen (last → first / first
 *      → last).
 *   3. Beim Unmount: Fokus zurück auf das vorher aktive Element (z.B. den
 *      Trigger-Button, der das Modal geöffnet hat) — verhindert, dass der
 *      Tastatur-User „den Faden verliert".
 *
 * KEIN eigenes ESC-Handling — das gehört in die jeweilige Modal-Komponente,
 * weil ESC-Verhalten kontextspezifisch ist (Schließen vs. Bestätigung
 * verlangen).
 *
 * Inertness:
 *   - Wir setzen kein `inert` auf den Rest der Seite — das wäre die
 *     korrekteste Lösung, ist aber browser-kompatibilitäts-fragil. Stattdessen
 *     setzen wir nur den Fokus-Trap und verlassen uns darauf, dass das Modal
 *     visuell als Overlay sichtbar ist.
 */

import { useEffect, useRef, type RefObject } from 'react';

/** CSS-Selektor für fokussierbare Elemente. Bewusst pragmatisch gehalten. */
const FOCUSABLE_SELECTOR = [
  'a[href]:not([disabled])',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'details',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface UseFocusTrapOptions {
  /**
   * Wenn false, ist der Trap deaktiviert (z.B. wenn das Modal noch nicht
   * sichtbar ist). Default true.
   */
  readonly active?: boolean;
  /**
   * Optional: explizites Ziel für den Initial-Fokus. Wenn nicht gesetzt,
   * fokussieren wir das erste fokussierbare Element im Container.
   */
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * Bindet einen Tab-Trap an den übergebenen Container.
 *
 * Verwendung:
 * ```tsx
 * const dialogRef = useRef<HTMLDivElement>(null);
 * useFocusTrap(dialogRef, { active: isOpen });
 * return <div ref={dialogRef} role="dialog" aria-modal="true">…</div>;
 * ```
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  opts: UseFocusTrapOptions = {},
): void {
  const { active = true, initialFocusRef } = opts;
  // Element, das vor dem Modal-Open den Fokus hatte — beim Close zurückgeben
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    // Vorhergehenden Fokus speichern (defensive: documentElement falls null)
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;

    // Initial-Fokus
    const target =
      initialFocusRef?.current ??
      container.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      container;
    // Kleines Timeout, damit React-Render abgeschlossen ist und Browser den
    // Fokus-Wechsel nicht verschluckt (besonders bei Modal-Open via Click).
    const focusTimer = window.setTimeout(() => {
      target.focus({ preventScroll: false });
    }, 0);

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      if (!container) return;

      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);

      if (focusables.length === 0) {
        // Nichts zu fokussieren — Tab nicht propagieren
        e.preventDefault();
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey) {
        // Rückwärts: wenn first oder Container → wrap zu last
        if (active === first || !container.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        // Vorwärts: wenn last → wrap zu first
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);
      // Fokus zurückgeben — nur wenn das Element noch im DOM ist und nicht
      // disabled.
      const restore = restoreFocusRef.current;
      if (restore && document.body.contains(restore)) {
        restore.focus();
      }
    };
  }, [active, containerRef, initialFocusRef]);
}
